import type {
  YouTubePreviewRequest,
  YouTubePreviewResponse,
  AddVideoRequest,
  AddVideoResponse,
  GetProjectVideosResponse,
  GetVideoByIDResponse,
  YouTubeEngagementSummary,
  YouTubeQuota,
} from '@/types/youtube.types'
import type {
  CreateYouTubeSegmentRequest,
  CreateYouTubeSegmentResponse,
  GetVideoSegmentsResponse,
  YouTubeVideoTranscriptsResponse,
  UpdateYouTubeSegmentRequest,
} from '@/types/youtubeSegments.types'
import type {
  YouTubeCaptionsRequest,
  YouTubeCaptionsResponse,
  YouTubeUserTranscriptResponse,
} from '@/types/youtubeCaptions.types'
import type {
  YouTubeTranslationsRequest,
  YouTubeTranslationsResponse,
  YouTubeUserTranslationResponse,
} from '@/types/youtubeTranslations.types'
import apiClient from '@/services/api'

export const previewVideo = async (request: YouTubePreviewRequest): Promise<YouTubePreviewResponse> => {
  const { data } = await apiClient.post<YouTubePreviewResponse>('/youtube/preview', request)
  return data
}

export const addVideo = async (request: AddVideoRequest): Promise<AddVideoResponse> => {
  const { data } = await apiClient.post<AddVideoResponse>('/youtube/videos', request)
  return data
}

export const getProjectVideos = async (projectId: number): Promise<GetProjectVideosResponse> => {
  const { data } = await apiClient.get<GetProjectVideosResponse>('/youtube/videos', {
    params: { project_id: projectId },
  })
  return data
}

export const deleteVideo = async (videoId: string): Promise<void> => {
  await apiClient.delete(`/youtube/videos/${videoId}`)
}

export const getVideoByID = async (videoId: string): Promise<GetVideoByIDResponse> => {
  const { data } = await apiClient.get<GetVideoByIDResponse>(`/youtube/videos/${videoId}`)
  return data
}

// ── YouTube Segment Functions (Story 2.2) ───────────────────────────────────
// These are independent of segments.service.ts and must NOT route through
// the podcast segment endpoints.

/**
 * Create a YouTube-backed segment for a saved video.
 * Calls POST /services/v2/youtube/segments.
 */
export const createYouTubeSegment = async (
  request: CreateYouTubeSegmentRequest,
): Promise<CreateYouTubeSegmentResponse> => {
  const { data } = await apiClient.post<CreateYouTubeSegmentResponse>('/youtube/segments', request)
  return data
}

/**
 * Fetch all segments for a saved YouTube video.
 * Calls GET /services/v2/youtube/segments?video_id=<uuid>.
 */
export const getVideoSegments = async (videoId: string): Promise<GetVideoSegmentsResponse> => {
  const { data } = await apiClient.get<GetVideoSegmentsResponse>('/youtube/segments', {
    params: { video_id: videoId },
  })
  return data
}
/**
 * Delete a YouTube-backed segment by its integer ID.
 * Calls DELETE /services/v2/youtube/segments/:id.
 * DB CASCADE constraints remove associated transcript and translation rows automatically.
 */
export const deleteYouTubeSegment = async (segmentId: number): Promise<void> => {
  await apiClient.delete(`/youtube/segments/${segmentId}`)
}

/**
 * Update start_time and end_time for an existing YouTube-backed segment.
 * Calls PATCH /services/v2/youtube/segments/:id.
 */
export const updateYouTubeSegment = async (
  segmentId: number,
  request: UpdateYouTubeSegmentRequest,
): Promise<void> => {
  await apiClient.patch(`/youtube/segments/${segmentId}`, request)
}

// ── YouTube Caption Functions (Story 3.1) ───────────────────────────────────
// Caption fetch is ALWAYS user-initiated. No auto-fetch on render or segment change.
// Do NOT use transcripts.service.ts or /auto/transcribe for YouTube captions.

/**
 * Fetch (or return cached) YouTube captions for a saved segment.
 * Calls POST /services/v2/youtube/captions with only { segment_id }.
 * Timestamps are resolved on the backend and must NOT be sent from the client.
 */
export const fetchYouTubeCaption = async (
  request: YouTubeCaptionsRequest,
): Promise<YouTubeCaptionsResponse> => {
  const { data } = await apiClient.post<YouTubeCaptionsResponse>('/youtube/captions', request)
  return data
}

// ── YouTube User Transcript Functions (Story 3.2) ────────────────────────────
// Editing is user-initiated and uses a manual debounce (5s idle) rather than
// TanStack Query useMutation — consistent with the Story 3.1 philosophy of
// explicit state management for the YouTube study flow.

/**
 * Upsert the user's edited transcript for a segment.
 * Calls PUT /services/v2/youtube/transcripts with { segment_id, text }.
 * Creates a source='user' row if none exists; overwrites if one does.
 * The source='youtube' row is never modified.
 */
export const saveYouTubeUserTranscript = async (
  segmentId: number,
  text: string,
): Promise<YouTubeUserTranscriptResponse> => {
  const { data } = await apiClient.put<YouTubeUserTranscriptResponse>('/youtube/transcripts', {
    segment_id: segmentId,
    text,
  })
  return data
}

/**
 * Soft-delete the user's edited transcript for a segment.
 * Calls DELETE /services/v2/youtube/transcripts with { segment_id }.
 * Idempotent: no error if no source='user' row exists.
 * After this, the source='youtube' row becomes the display transcript again.
 */
export const deleteYouTubeUserTranscript = async (segmentId: number): Promise<void> => {
  await apiClient.delete('/youtube/transcripts', {
    data: { segment_id: segmentId },
  })
}

/**
 * Fetch all source='user' transcripts for a saved YouTube video by its internal UUID.
 * Calls GET /services/v2/youtube/transcripts?video_id=<uuid>.
 * Results are ordered ascending by segment start_time.
 * Used by the practice-reading download modal — no yt-dlp or external API involved.
 */
export const getYouTubeVideoUserTranscripts = async (
  videoId: string,
): Promise<YouTubeVideoTranscriptsResponse> => {
  const { data } = await apiClient.get<YouTubeVideoTranscriptsResponse>('/youtube/transcripts', {
    params: { video_id: videoId },
  })
  return data
}

// ── YouTube Translation Functions (Story 3.3) ────────────────────────────────
// Translation fetch is ALWAYS user-initiated. No auto-fetch on render or segment change.
// Do NOT use translateService.ts or /auto/translate for YouTube segment translations.

/**
 * Fetch (or return cached) YouTube translation for a saved segment.
 * Calls POST /services/v2/youtube/translations with { segment_id }.
 * Timestamps are resolved on the backend and must NOT be sent from the client.
 */
export const fetchYouTubeTranslation = async (
  request: YouTubeTranslationsRequest,
): Promise<YouTubeTranslationsResponse> => {
  const { data } = await apiClient.post<YouTubeTranslationsResponse>('/youtube/translations', request)
  return data
}

/**
 * Upsert the user's edited translation for a segment.
 * Calls PUT /services/v2/youtube/translations with { segment_id, text }.
 * Creates a source='user' row if none exists; overwrites if one does.
 * The source='youtube' and source='google_translate' rows are never modified.
 */
export const saveYouTubeUserTranslation = async (
  segmentId: number,
  text: string,
): Promise<YouTubeUserTranslationResponse> => {
  const { data } = await apiClient.put<YouTubeUserTranslationResponse>('/youtube/translations', {
    segment_id: segmentId,
    text,
  })
  return data
}

/**
 * Soft-delete the user's edited translation for a segment.
 * Calls DELETE /services/v2/youtube/translations with { segment_id }.
 * Idempotent: no error if no source='user' row exists.
 * After this, the highest-priority auto source (youtube or google_translate)
 * becomes the display translation again.
 */
export const deleteYouTubeUserTranslation = async (segmentId: number): Promise<void> => {
  await apiClient.delete('/youtube/translations', {
    data: { segment_id: segmentId },
  })
}

/**
 * Fetch aggregated engagement counters (segments created, vocab saved, total videos)
 * for the authenticated user across all their YouTube content.
 * Calls GET /services/v2/youtube/engagement/summary.
 */
export const getEngagementSummary = async (): Promise<YouTubeEngagementSummary> => {
  const { data } = await apiClient.get<YouTubeEngagementSummary>('/youtube/engagement/summary')
  return data
}

// ── YouTube Quota Functions (Story 3.4) ─────────────────────────────────────

/**
 * Fetch the authenticated user's auto-translation quota balance.
 * Calls GET /services/v2/youtube/quota.
 * Used by the no-captions panel to show remaining translation quota.
 */
export const getYouTubeQuota = async (): Promise<YouTubeQuota> => {
  const { data } = await apiClient.get<YouTubeQuota>('/youtube/quota')
  return data
}
